import { faPen, faSave, faTimes } from "@fortawesome/free-solid-svg-icons"
import { Loading, Pagination, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import { AIMS_BAND_COUNT_OPTIONS, AIMS_BAND_COUNT_VALUES } from "constants/aims"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useMemo, useState } from "react"
import Select from "react-select"
import {
  AssetAnalysisRound,
  AssetStatusRecord,
  useListAssetStatusesQuery,
  useListRoundsQuery,
  usePatchRoundMutation,
} from "services/aimsBackend"

type RoundDraft = {
  analysis_year: string
  status_id: string
  observation: string
  image_date: string
  image_id: string
  copyright: string
  band_count: string
  image_clearance: boolean
}

type Props = {
  assetId: number
  active: boolean
  contractType: "ADA" | "LIA"
  emptyMessage: string
}

function isBaselineStatus(statusName?: string): boolean {
  return statusName?.toLowerCase() === "baseline"
}

function roundToDraft(round: AssetAnalysisRound): RoundDraft {
  return {
    analysis_year: String(round.analysis_year ?? ""),
    status_id: round.status?.id ? String(round.status.id) : "",
    observation: round.observation ?? "",
    image_date: round.image_date ?? "",
    image_id: round.image_id ?? "",
    copyright: round.copyright ?? "",
    band_count: round.band_count ? String(round.band_count) : "",
    image_clearance: Boolean(round.image_clearance),
  }
}

function statusAppliesToRound(status: AssetStatusRecord, roundNumber: number): boolean {
  const { min_round_number: minRound, max_round_number: maxRound } = status
  return (minRound == null || minRound <= roundNumber) && (maxRound == null || maxRound >= roundNumber)
}

function apiErrorMessage(reason: unknown): string {
  const data = (reason as any)?.data
  if (typeof data === "string") return data
  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([field, detail]) => `${field}: ${Array.isArray(detail) ? detail.join(", ") : String(detail)}`)
      .join("; ")
  }
  return "Failed to save round"
}

export default function EditableRoundsTab({ assetId, active, contractType, emptyMessage }: Props) {
  const { showSnackBar } = useCommon()
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [drafts, setDrafts] = useState<Record<number, RoundDraft>>({})
  const { data: roundsList, isFetching } = useListRoundsQuery(
    { asset: assetId, contract_type: contractType, page: pageNumber, page_size: pageSize },
    { skip: !active, refetchOnMountOrArgChange: true },
  )
  const { data: statuses = [] } = useListAssetStatusesQuery({ contract_type: contractType }, { skip: !active })
  const [patchRound] = usePatchRoundMutation()

  const rounds = useMemo(() => roundsList?.results ?? [], [roundsList])
  const changedRounds = useMemo(
    () =>
      rounds.filter(
        round => drafts[round.id] && JSON.stringify(drafts[round.id]) !== JSON.stringify(roundToDraft(round)),
      ),
    [drafts, rounds],
  )

  const startEdit = () => {
    setDrafts(Object.fromEntries(rounds.map(round => [round.id, roundToDraft(round)])))
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setDrafts({})
    setIsEditing(false)
  }

  const updateDraft = (roundId: number, field: keyof RoundDraft, value: string | boolean) => {
    setDrafts(prev => ({ ...prev, [roundId]: { ...prev[roundId], [field]: value } }))
  }

  const statusOptionsForRound = (round: AssetAnalysisRound) =>
    statuses
      .filter(status => !isBaselineStatus(status.name) && statusAppliesToRound(status, round.round_number))
      .map(status => ({ value: String(status.id), label: status.name }))

  const saveChanges = async () => {
    const invalidYear = changedRounds.find(round => !Number.isInteger(Number(drafts[round.id].analysis_year)))
    if (invalidYear) {
      showSnackBar(`${invalidYear.label}: enter a valid year`, "error")
      return
    }
    const invalidBands = changedRounds.find(round => {
      const bandCount = drafts[round.id].band_count
      return bandCount && !AIMS_BAND_COUNT_VALUES.includes(bandCount)
    })
    if (invalidBands) {
      showSnackBar(`${invalidBands.label}: bands must be empty, 1, or 3`, "error")
      return
    }
    if (changedRounds.length === 0) {
      cancelEdit()
      return
    }

    setIsSaving(true)
    const failures: string[] = []
    await changedRounds.reduce(
      (previous, round) =>
        previous.then(async () => {
          const draft = drafts[round.id]
          try {
            await patchRound({
              id: round.id,
              body: {
                analysis_year: Number(draft.analysis_year),
                ...(isBaselineStatus(round.status?.name)
                  ? {}
                  : { status_id: draft.status_id ? Number(draft.status_id) : null }),
                image_date: draft.image_date || null,
                image_id: draft.image_id,
                copyright: draft.copyright,
                band_count: draft.band_count ? Number(draft.band_count) : null,
                image_clearance: draft.image_clearance,
                observation: draft.observation,
              },
            }).unwrap()
          } catch (reason) {
            failures.push(`${round.label}: ${apiErrorMessage(reason)}`)
          }
        }),
      Promise.resolve(),
    )
    setIsSaving(false)

    if (failures.length > 0) {
      showSnackBar(`${changedRounds.length - failures.length} saved; ${failures.join(" | ")}`, "error")
      return
    }
    showSnackBar(`${changedRounds.length} round${changedRounds.length === 1 ? "" : "s"} saved`, "success")
    cancelEdit()
  }

  if (isFetching) {
    return (
      <div className="mt-3" style={{ minHeight: 200 }}>
        <Loading active />
      </div>
    )
  }

  if (rounds.length === 0) {
    return (
      <div className="mt-3" style={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p className="text-gray-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-end mb-2" style={{ gap: 8 }}>
        {isEditing ? (
          <>
            <Button
              action={saveChanges}
              label={isSaving ? "Saving..." : `Save changes${changedRounds.length ? ` (${changedRounds.length})` : ""}`}
              icon={faSave}
              disabled={isSaving}
              className="btn-full green"
            />
            <Button action={cancelEdit} label="Cancel" icon={faTimes} disabled={isSaving} className="btn-full" />
          </>
        ) : (
          <Button action={startEdit} label="Edit table" icon={faPen} className="btn-full blue" />
        )}
      </div>

      <div className="aims-rounds-table-wrap">
        <table className="aims-rounds-html-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Asset ID</th>
              <th>AIMS ID</th>
              <th>Round Count</th>
              <th>Round</th>
              <th>Status</th>
              <th>Observation</th>
              <th>Image Date</th>
              <th>Image ID</th>
              <th>Copyright</th>
              <th>Bands</th>
              <th>Image Clearance</th>
            </tr>
          </thead>
          <tbody>
            {rounds.map(row => {
              const draft = drafts[row.id]
              const statusOptions = statusOptionsForRound(row)
              return (
                <tr key={row.id}>
                  <td>
                    {isEditing ? (
                      <TextInput
                        labelText=""
                        hideLabel
                        value={draft.analysis_year}
                        onChange={e => updateDraft(row.id, "analysis_year", e.target.value)}
                        placeholder="Year"
                      />
                    ) : (
                      row.analysis_year ?? "-"
                    )}
                  </td>
                  <td>{row.AssetId || "-"}</td>
                  <td>{row.aims_id}</td>
                  <td>{row.round_number}</td>
                  <td>{row.label}</td>
                  <td>
                    {isEditing && !isBaselineStatus(row.status?.name) ? (
                      <Select
                        classNamePrefix="wfp--react-select"
                        className="wfp--react-select-container"
                        options={statusOptions}
                        value={statusOptions.find(option => option.value === draft.status_id) || null}
                        onChange={option => updateDraft(row.id, "status_id", option?.value ?? "")}
                        placeholder="Select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        menuPosition="fixed"
                        isClearable
                      />
                    ) : (
                      row.status?.name ?? "-"
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <TextInput
                        labelText=""
                        hideLabel
                        value={draft.observation}
                        onChange={e => updateDraft(row.id, "observation", e.target.value)}
                        placeholder="Observation"
                      />
                    ) : (
                      row.observation || "-"
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <TextInput
                        labelText=""
                        hideLabel
                        type={"date" as any}
                        value={draft.image_date}
                        onChange={e => updateDraft(row.id, "image_date", e.target.value)}
                      />
                    ) : (
                      row.image_date || "-"
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <TextInput
                        labelText=""
                        hideLabel
                        value={draft.image_id}
                        onChange={e => updateDraft(row.id, "image_id", e.target.value)}
                        placeholder="Image ID"
                      />
                    ) : (
                      row.image_id || "-"
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <TextInput
                        labelText=""
                        hideLabel
                        value={draft.copyright}
                        onChange={e => updateDraft(row.id, "copyright", e.target.value)}
                        placeholder="Copyright"
                      />
                    ) : (
                      row.copyright || "-"
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <Select
                        classNamePrefix="wfp--react-select"
                        className="wfp--react-select-container"
                        options={AIMS_BAND_COUNT_OPTIONS}
                        value={
                          AIMS_BAND_COUNT_OPTIONS.find(option => option.value === draft.band_count) ||
                          AIMS_BAND_COUNT_OPTIONS[0]
                        }
                        onChange={option => updateDraft(row.id, "band_count", option?.value ?? "")}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        menuPosition="fixed"
                      />
                    ) : (
                      row.band_count ?? "-"
                    )}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label="Image clearance"
                      checked={isEditing ? draft.image_clearance : row.image_clearance}
                      onChange={e => updateDraft(row.id, "image_clearance", e.target.checked)}
                      disabled={!isEditing}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        totalItems={roundsList?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[10, 20, 50]}
        page={pageNumber}
        onChange={(event: any): void => {
          cancelEdit()
          const nextPageSize = (event as any).pageSize || pageSize
          setPageSize(nextPageSize)
          setPageNumber(nextPageSize === pageSize ? (event as any).page || 1 : 1)
        }}
      />
    </div>
  )
}
